import { useState, type FormEvent } from 'react';
import { Flame, Inbox, Search, Target, TrendingUp, UserPlus } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  INQUIRY_ACTIVITY_LABEL,
  INQUIRY_PRIORITY_LABEL,
  INQUIRY_STATUS_LABEL,
  INQUIRY_TRANSITIONS,
  useAssignInquiry,
  useChangeInquiryStatus,
  useInquiries,
  useInquiry,
  useInquirySummary,
  useLogInquiryActivity,
  type InquiryActivityType,
  type InquiryPriority,
  type InquiryStatus,
} from '@/lib/crm-api';
import { ROLE_LABEL, useStaffDirectory } from '@/lib/users-api';
import { cn, errorMessage, formatCurrency, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * The sales pipeline (spec §18, §35).
 *
 * Ordered urgent-first then oldest-first by the API, because a lead going cold
 * is the failure this screen exists to prevent — not a lead being unsorted.
 *
 * Everything a lead's owner does is recorded as an activity, including status
 * changes and assignments, so the drawer's timeline is the whole story of how
 * the deal was worked rather than a note field somebody remembered to fill in.
 */

const STATUS_TABS: Array<{ label: string; value: InquiryStatus[] | null; unassigned?: boolean }> = [
  { label: 'All', value: null },
  { label: 'Unworked', value: null, unassigned: true },
  { label: 'New', value: ['NEW'] },
  { label: 'In progress', value: ['CONTACTED', 'QUALIFIED', 'QUOTATION_SENT', 'NEGOTIATION'] },
  { label: 'Won', value: ['WON'] },
  { label: 'Lost', value: ['LOST', 'CLOSED'] },
];

const PRIORITY_TONE: Record<InquiryPriority, 'neutral' | 'brand' | 'warning' | 'danger'> = {
  LOW: 'neutral',
  MEDIUM: 'brand',
  HIGH: 'warning',
  URGENT: 'danger',
};

type PanelMode = 'status' | 'assign' | 'activity';

export function InquiriesPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTab = STATUS_TABS[tab] ?? STATUS_TABS[0]!;

  const inquiries = useInquiries({
    page,
    limit: 20,
    search,
    status: activeTab.value ?? undefined,
    unassignedOnly: activeTab.unassigned,
  });
  const summary = useInquirySummary();

  return (
    <div>
      <PageHeader
        title="Bulk Inquiries"
        description="Every lead, who owns it and where it has reached."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="New leads"
              value={formatNumber(summary.data?.['new'] ?? 0)}
              icon={<Inbox className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Nobody working them"
              value={formatNumber(summary.data?.['unassigned'] ?? 0)}
              icon={<Flame className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Won"
              value={formatNumber(summary.data?.['won'] ?? 0)}
              icon={<Target className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Win rate"
              value={`${summary.data?.['winRatePercent'] ?? 0}%`}
              icon={<TrendingUp className="size-5" aria-hidden="true" />}
              tone="success"
            />
          </>
        )}
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search number, name, company, email or phone"
              className="pl-9"
              aria-label="Search inquiries"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Lead status">
            {STATUS_TABS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={tab === index}
                onClick={() => {
                  setTab(index);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === index
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        {inquiries.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : inquiries.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load inquiries">
              {errorMessage(inquiries.error)}
            </ErrorAlert>
          </div>
        ) : (inquiries.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No leads match"
            description="Try a different status or clear the search."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Lead</Th>
                <Th>Contact</Th>
                <Th>Requirement</Th>
                <Th>Priority</Th>
                <Th>Status</Th>
                <Th>Owner</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {inquiries.data?.items.map((inquiry) => (
                <tr key={inquiry.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{inquiry.inquiryNumber}</p>
                    <p className="text-xs text-ink-subtle">{formatDateTime(inquiry.createdAt)}</p>
                  </Td>
                  <Td>
                    <p className="text-ink">{inquiry.name}</p>
                    <p className="text-xs text-ink-subtle">
                      {inquiry.companyName ?? inquiry.email}
                    </p>
                  </Td>
                  <Td className="text-sm text-ink-muted">
                    {inquiry.productInterest ?? '—'}
                    {inquiry.quantity !== null && (
                      <span className="block text-xs text-ink-subtle">
                        {formatNumber(inquiry.quantity)} pcs
                        {inquiry.budget !== null && ` · ${formatCurrency(inquiry.budget)}`}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={PRIORITY_TONE[inquiry.priority]}>
                      {INQUIRY_PRIORITY_LABEL[inquiry.priority]}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(inquiry.status)}>
                      {INQUIRY_STATUS_LABEL[inquiry.status]}
                    </Badge>
                  </Td>
                  <Td className="text-sm text-ink-muted">
                    {inquiry.assignedTo?.name ?? (
                      <span className="text-warning-700">Unassigned</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(inquiry.id)}>
                      Work it
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {inquiries.data && inquiries.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {inquiries.data.pagination.total} leads · page {inquiries.data.pagination.page} of{' '}
              {inquiries.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!inquiries.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!inquiries.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <InquiryDrawer inquiryId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function InquiryDrawer({
  inquiryId,
  onClose,
}: {
  inquiryId: string | null;
  onClose: () => void;
}) {
  const detail = useInquiry(inquiryId);
  const [mode, setMode] = useState<PanelMode | null>(null);

  const inquiry = detail.data;
  const nextStatuses = inquiry ? INQUIRY_TRANSITIONS[inquiry.status] : [];

  return (
    <Drawer
      open={Boolean(inquiryId)}
      onClose={() => {
        setMode(null);
        onClose();
      }}
      title={inquiry?.inquiryNumber ?? 'Inquiry'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this lead">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        inquiry && (
          <div className="space-y-5">
            <section>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge tone={statusTone(inquiry.status)}>
                  {INQUIRY_STATUS_LABEL[inquiry.status]}
                </Badge>
                <Badge tone={PRIORITY_TONE[inquiry.priority]}>
                  {INQUIRY_PRIORITY_LABEL[inquiry.priority]}
                </Badge>
                <Badge tone="neutral">{inquiry.source.replace(/_/g, ' ').toLowerCase()}</Badge>
              </div>

              <p className="font-medium text-ink">{inquiry.name}</p>
              {inquiry.companyName && (
                <p className="text-sm text-ink-muted">{inquiry.companyName}</p>
              )}
              <p className="mt-1 text-sm text-ink-muted">
                <a href={`mailto:${inquiry.email}`} className="text-brand-600">
                  {inquiry.email}
                </a>{' '}
                ·{' '}
                <a href={`tel:${inquiry.phone}`} className="text-brand-600">
                  {inquiry.phone}
                </a>
              </p>

              {inquiry.customer ? (
                <p className="mt-2 text-xs text-ink-subtle">
                  Registered buyer · {inquiry.customer.customerCode}
                </p>
              ) : (
                <p className="mt-2 text-xs text-ink-subtle">No account — anonymous enquiry.</p>
              )}
            </section>

            <section className="rounded-lg border border-line p-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                {(
                  [
                    ['Product', inquiry.product?.name ?? inquiry.productInterest ?? '—'],
                    [
                      'Quantity',
                      inquiry.quantity === null ? '—' : `${formatNumber(inquiry.quantity)} pcs`,
                    ],
                    ['Budget', inquiry.budget === null ? '—' : formatCurrency(inquiry.budget)],
                    ['Deliver to', inquiry.deliveryLocation ?? '—'],
                  ] as Array<[string, string]>
                ).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-ink-subtle">{label}</dt>
                    <dd className="font-medium text-ink">{value}</dd>
                  </div>
                ))}
              </dl>

              {inquiry.customizationRequirement && (
                <p className="mt-3 text-sm text-ink-muted">
                  <span className="text-xs text-ink-subtle">Customisation: </span>
                  {inquiry.customizationRequirement}
                </p>
              )}

              {inquiry.message && (
                <p className="mt-3 whitespace-pre-wrap rounded-lg bg-canvas p-3 text-sm text-ink-muted">
                  {inquiry.message}
                </p>
              )}
            </section>

            {mode ? (
              <InquiryActionForm
                mode={mode}
                inquiry={inquiry}
                nextStatuses={nextStatuses}
                onDone={() => setMode(null)}
              />
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    disabled={nextStatuses.length === 0}
                    onClick={() => setMode('status')}
                    title={
                      nextStatuses.length === 0 ? 'This lead is closed.' : undefined
                    }
                  >
                    Move stage
                  </Button>
                  <Button variant="outline" onClick={() => setMode('assign')}>
                    <UserPlus className="size-4" aria-hidden="true" />
                    Assign
                  </Button>
                  <Button variant="outline" className="col-span-2" onClick={() => setMode('activity')}>
                    Log a call or note
                  </Button>
                </div>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Activity</h3>
              {inquiry.activities.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-subtle">
                  Nothing recorded yet.
                </p>
              ) : (
                <ol className="space-y-2">
                  {inquiry.activities.map((activity) => (
                    <li key={activity.id} className="rounded-lg border border-line p-3 text-sm">
                      <p className="font-medium text-ink">
                        {INQUIRY_ACTIVITY_LABEL[activity.type]}
                      </p>
                      {activity.note && (
                        <p className="mt-1 whitespace-pre-wrap text-ink-muted">{activity.note}</p>
                      )}
                      <p className="mt-1.5 text-xs text-ink-subtle">
                        {formatDateTime(activity.createdAt)}
                        {activity.performedBy && ` · ${activity.performedBy.name}`}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )
      )}
    </Drawer>
  );
}

/* ==========================================================================
   One form, shared by every action
   ========================================================================== */

const ACTIVITY_TYPES: InquiryActivityType[] = ['NOTE', 'CALL', 'EMAIL', 'MEETING'];

function InquiryActionForm({
  mode,
  inquiry,
  nextStatuses,
  onDone,
}: {
  mode: PanelMode;
  inquiry: { id: string; priority: InquiryPriority; assignedTo: { id: string } | null };
  nextStatuses: readonly InquiryStatus[];
  onDone: () => void;
}) {
  const changeStatus = useChangeInquiryStatus();
  const assign = useAssignInquiry();
  const logActivity = useLogInquiryActivity();
  // Only fetched when the assign panel is open; the other two do not need it.
  const directory = useStaffDirectory();

  const mutation = mode === 'status' ? changeStatus : mode === 'assign' ? assign : logActivity;

  const [status, setStatus] = useState<string>(nextStatuses[0] ?? '');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<InquiryPriority>(inquiry.priority);
  const [assignedToId, setAssignedToId] = useState(inquiry.assignedTo?.id ?? '');
  const [activityType, setActivityType] = useState<InquiryActivityType>('CALL');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const onSuccess = () => onDone();

    if (mode === 'status') {
      changeStatus.mutate(
        {
          inquiryId: inquiry.id,
          status: status as InquiryStatus,
          // A lost lead has to say why; the reason is stored on the record.
          ...(status === 'LOST' ? { reason: notes } : { notes: notes || undefined }),
        },
        { onSuccess },
      );
      return;
    }

    if (mode === 'assign') {
      assign.mutate(
        {
          inquiryId: inquiry.id,
          assignedToId: assignedToId || undefined,
          priority,
        },
        { onSuccess },
      );
      return;
    }

    logActivity.mutate(
      { inquiryId: inquiry.id, type: activityType, note: notes },
      { onSuccess },
    );
  };

  return (
    <section className="rounded-lg border border-line p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">
        {mode === 'status' ? 'Move this lead on' : mode === 'assign' ? 'Assign the lead' : 'Log activity'}
      </h3>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {mutation.isError && (
          <ErrorAlert title="Could not do that">{errorMessage(mutation.error)}</ErrorAlert>
        )}

        {mode === 'status' && (
          <Field
            label="New stage"
            htmlFor="status"
            required
            hint="Only the moves this lead's pipeline allows are listed."
          >
            <Select
              id="status"
              required
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              {nextStatuses.map((next) => (
                <option key={next} value={next}>
                  {INQUIRY_STATUS_LABEL[next]}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {mode === 'assign' && (
          <>
            <Field
              label="Owner"
              htmlFor="assignedToId"
              hint={
                directory.isError
                  ? 'Could not load the staff list — needs the user.view permission.'
                  : 'Leave unassigned to put the lead back in the pool.'
              }
            >
              <Select
                id="assignedToId"
                value={assignedToId}
                onChange={(event) => setAssignedToId(event.target.value)}
                disabled={directory.isLoading}
              >
                <option value="">Unassigned</option>
                {(directory.data?.items ?? []).map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} — {ROLE_LABEL[member.role]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Priority" htmlFor="priority">
              <Select
                id="priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value as InquiryPriority)}
              >
                {(Object.keys(INQUIRY_PRIORITY_LABEL) as InquiryPriority[]).map((value) => (
                  <option key={value} value={value}>
                    {INQUIRY_PRIORITY_LABEL[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}

        {mode === 'activity' && (
          <Field label="What happened?" htmlFor="activityType" required>
            <Select
              id="activityType"
              value={activityType}
              onChange={(event) => setActivityType(event.target.value as InquiryActivityType)}
            >
              {ACTIVITY_TYPES.map((value) => (
                <option key={value} value={value}>
                  {INQUIRY_ACTIVITY_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {mode !== 'assign' && (
          <Field
            label={mode === 'status' && status === 'LOST' ? 'Why was it lost?' : 'Notes'}
            htmlFor="notes"
            required={mode === 'activity' || (mode === 'status' && status === 'LOST')}
          >
            <Textarea
              id="notes"
              rows={3}
              required={mode === 'activity' || (mode === 'status' && status === 'LOST')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={
                mode === 'activity'
                  ? 'Called; asked for a revised price on 1000 pieces.'
                  : 'Recorded against the lead.'
              }
            />
          </Field>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Confirm'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
